<template>
  <div class="dashboard-container">
    <div class="container">
      <div class="tableBar">
        <h3>PC端价格上浮配置</h3>
        <p style="color: #666; margin: 10px 0 20px 0;">
          设置PC端（端游区）商品价格的上浮比例，默认值为20%。此配置将影响所有PC端商品的最终价格。
        </p>
      </div>

      <div v-loading="loading" class="config-container">
        <el-card class="config-card">
          <div slot="header" class="clearfix">
            <span>当前配置</span>
          </div>

          <div v-if="config" class="config-content">
            <el-row :gutter="20">
              <el-col :span="12">
                <div class="config-item">
                  <label>上浮百分比：</label>
                  <span class="value">{{ config.markupPercentage }}%</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="config-item">
                  <label>状态：</label>
                  <el-tag :type="config.status === 1 ? 'success' : 'danger'">
                    {{ config.status === 1 ? '启用' : '禁用' }}
                  </el-tag>
                </div>
              </el-col>
            </el-row>

            <el-row :gutter="20" style="margin-top: 20px;">
              <el-col :span="24">
                <div class="config-item">
                  <label>描述：</label>
                  <span class="description">{{ config.description || '无描述' }}</span>
                </div>
              </el-col>
            </el-row>

            <el-row :gutter="20" style="margin-top: 20px;">
              <el-col :span="12">
                <div class="config-item">
                  <label>创建时间：</label>
                  <span class="value">{{ formatDate(config.createTime) }}</span>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="config-item">
                  <label>更新时间：</label>
                  <span class="value">{{ formatDate(config.updateTime) }}</span>
                </div>
              </el-col>
            </el-row>
          </div>

          <div v-else class="no-config">
            <p>暂无配置信息</p>
          </div>
        </el-card>

        <el-card class="config-card" style="margin-top: 20px;">
          <div slot="header" class="clearfix">
            <span>修改配置</span>
            <el-button style="float: right; padding: 3px 0" type="text" @click="editConfig">编辑</el-button>
          </div>

          <div class="edit-form">
            <el-form
              ref="configForm"
              :model="form"
              :rules="rules"
              label-width="120px"
            >
              <el-form-item label="上浮百分比" prop="markupPercentage">
                <el-input-number
                  v-model="form.markupPercentage"
                  :min="0"
                  :max="100"
                  :precision="2"
                  controls-position="right"
                  style="width: 200px;"
                />
                <span style="margin-left: 10px; color: #999;">%</span>
                <span style="margin-left: 20px; color: #666; font-size: 12px;">
                  例如：20 表示价格上浮20%，最终价格 = 原价 × 1.2
                </span>
              </el-form-item>

              <el-form-item label="状态" prop="status">
                <el-select v-model="form.status" style="width: 200px;">
                  <el-option label="启用" :value="1" />
                  <el-option label="禁用" :value="0" />
                </el-select>
              </el-form-item>

              <el-form-item label="描述">
                <el-input
                  v-model="form.description"
                  type="textarea"
                  :rows="3"
                  placeholder="请输入配置描述（可选）"
                  maxlength="255"
                  show-word-limit
                />
              </el-form-item>

              <el-form-item>
                <el-button type="primary" :loading="submitLoading" @click="submitConfig">
                  保存配置
                </el-button>
                <el-button @click="resetForm">重置</el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { getPcPriceMarkupActive, updatePcPriceMarkup } from '@/api/index'

export default {
  name: 'PcPriceMarkup',
  data() {
    return {
      loading: false,
      submitLoading: false,
      config: null,
      form: {
        id: null,
        markupPercentage: 20.00,
        status: 1,
        description: ''
      },
      rules: {
        markupPercentage: [
          { required: true, message: '请输入上浮百分比', trigger: 'blur' },
          { type: 'number', min: 0, max: 100, message: '上浮百分比必须在0-100之间', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    // 加载当前配置
    async loadConfig() {
      this.loading = true
      try {
        const res = await getPcPriceMarkupActive()
        if (res.data && res.data.code === 1) {
          this.config = res.data.data
          // 初始化表单数据
          this.resetForm()
        } else {
          this.$message.warning('获取配置失败')
        }
      } catch (error) {
        console.error('获取PC端价格上浮配置失败:', error)
        this.$message.error('获取配置失败')
      } finally {
        this.loading = false
      }
    },

    // 编辑配置
    editConfig() {
      if (this.config) {
        this.form = {
          id: this.config.id,
          markupPercentage: this.config.markupPercentage,
          status: this.config.status,
          description: this.config.description || ''
        }
      }
    },

    // 提交配置
    async submitConfig() {
      try {
        await this.$refs.configForm.validate()
      } catch (error) {
        return
      }

      this.submitLoading = true
      try {
        const res = await updatePcPriceMarkup(this.form)
        if (res.data && res.data.code === 1) {
          this.$message.success('配置更新成功')
          this.loadConfig() // 重新加载配置
        } else {
          this.$message.error((res.data && res.data.msg) || '更新配置失败')
        }
      } catch (error) {
        console.error('更新PC端价格上浮配置失败:', error)
        this.$message.error('更新配置失败')
      } finally {
        this.submitLoading = false
      }
    },

    // 重置表单
    resetForm() {
      if (this.config) {
        this.form = {
          id: this.config.id,
          markupPercentage: this.config.markupPercentage,
          status: this.config.status,
          description: this.config.description || ''
        }
      } else {
        this.form = {
          id: null,
          markupPercentage: 20.00,
          status: 1,
          description: ''
        }
      }
      this.$refs.configForm && this.$refs.configForm.clearValidate()
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 20px;
}

.container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
}

.tableBar {
  margin-bottom: 20px;

  h3 {
    margin: 0 0 10px 0;
    color: #333;
    font-size: 18px;
    font-weight: 500;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }
}

.config-container {
  .config-card {
    .config-content {
      .config-item {
        display: flex;
        align-items: center;
        margin-bottom: 15px;

        label {
          font-weight: 500;
          color: #666;
          min-width: 100px;
          margin-right: 10px;
        }

        .value {
          color: #333;
          font-weight: 500;
        }

        .description {
          color: #666;
          line-height: 1.5;
        }
      }
    }

    .no-config {
      text-align: center;
      color: #999;
      padding: 40px 0;
    }

    .edit-form {
      .el-form-item {
        margin-bottom: 20px;
      }
    }
  }
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}
</style>
